/**
 * Wiring. Owns the renderer, the world, the UI, the loop and the recording, and
 * keeps the fixed-step simulation strictly separated from rendering.
 *
 *   preUpdate()  - input, mouse look, one-shot actions
 *   step()       - build one StepInput, record it, hand it to RunSimulation
 *   render(a)    - interpolated visuals only; nothing here changes gameplay
 *
 * Note what step() does *not* do any more: it contains no gameplay. Every
 * gameplay decision lives in RunSimulation, which has no idea a browser exists,
 * so the server can run the identical code over the identical inputs and get
 * the identical time. Game's job is to decide what the inputs were - and to
 * write them down on the way past.
 */

import * as THREE from 'three';
import { WakeUpSequence } from './WakeUpSequence';
import { ComputerScreen } from '../ui/ComputerScreen';
import { WakeUpOverlay } from '../ui/WakeUpOverlay';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { FIXED_TIMESTEP, MOMENTUM, RENDER, SIMULATION_HZ } from '../config/gameplay';
import { KOKKI_LINE, RUN_START_LINES } from '../config/dialogue';
import {
  PSYCHEDELIC,
  describeIntoxication,
  sanitiseLevel,
  type IntoxicationLevel,
} from '../config/intoxication';
import { TEST_LEVEL } from '../config/level';
import { createSeededLevel } from '../sim/SeededLevel';
import { DEFAULT_SEED } from '../sim/seed';
import type { LevelDefinition } from '../world/Level';
import { PLAYER_NAME } from '../config/player';
import { MIX, PROJECTOR, SNORE } from '../config/audio';
import { WebAudioManager } from '../audio/WebAudioManager';
import { insideVenue, placedMix } from '../audio/karaoke';
import type { AudioManager } from '../audio/AudioManager';
import { CameraController, sitProgress } from '../player/CameraController';
import { sampleBeer } from '../player/Beer';
import { BeerViewmodel } from '../render/BeerViewmodel';
import { FistViewmodel } from '../render/FistViewmodel';
import { KlapiViewmodel } from '../render/KlapiViewmodel';
import { PlateViewmodel } from '../render/PlateViewmodel';
import { TuoppiViewmodel } from '../render/TuoppiViewmodel';
import { Projection } from '../render/Projection';
import { PsychedelicPass } from '../render/PsychedelicPass';
import { InputManager } from '../player/InputManager';
import { LookAccumulator } from '../player/LookAccumulator';
import { LEADERBOARD } from '../config/leaderboard';
import { LocalStorageScoreRepository } from '../score/LocalStorageScoreRepository';
import { HttpLeaderboardClient } from '../score/HttpLeaderboardClient';
import { OfflineLeaderboardClient, type LeaderboardClient } from '../score/Leaderboard';
import { RunSubmitter, type SubmissionState } from '../score/RunSubmitter';
import { hasChosenName } from '../score/playerName';
import { ReplayRecorder } from '../replay/ReplayRecorder';
import { PersonalRuns } from '../score/PersonalRuns';
import { ResultsScreen } from '../ui/ResultsScreen';
import { LeaderboardSplitsScreen } from '../ui/LeaderboardSplitsScreen';
import { loadReplay } from '../replay/loadReplay';
import type { Replay } from '../replay/ReplayFormat';
import { button, el } from '../ui/dom';
import { NullScoreRepository, verifyReplay } from '../replay/verifyReplay';
import { RunSimulation } from '../sim/RunSimulation';
import { buttonForPin, buttonsFromMovement, type StepInput } from '../sim/StepInput';
import { simulationFingerprint } from '../sim/fingerprint';
import { AamupalaOverlay } from '../ui/AamupalaOverlay';
import { BloodOverlay } from '../ui/BloodOverlay';
import { DebugOverlay } from '../ui/DebugOverlay';
import { FinishScreen } from '../ui/FinishScreen';
import { createHelpScreen } from '../ui/HelpScreen';
import { HUD } from '../ui/HUD';
import { RunObjective } from '../ui/RunObjective';
import type { MenuButtons } from '../ui/MenuButtons';
import { PauseScreen } from '../ui/PauseScreen';
import { ReadyScreen } from '../ui/ReadyScreen';
import { createSettingsScreen } from '../ui/SettingsScreen';
import { SettingsStore } from '../ui/SettingsStore';
import { clamp, vec3, type Vec3 } from '../utils/math';
import type { Interactable } from '../world/Interactable';
import { sleeperBox } from '../world/Level';
import { World } from '../render/World';
import { GameLoop } from './GameLoop';
import { ShoutDirector } from './ShoutDirector';
import { RunState } from './GameState';
import { RunManager, type FinishedRun } from './RunManager';

/**
 * Where the leaderboard API lives.
 *
 * Same origin in production (CloudFront routes /api/* to the API Gateway), so
 * the site's `connect-src 'self'` policy stays as tight as it is. `vite dev`
 * has no such route, so a dev build only talks to a backend when told to:
 *
 *   VITE_LANIAAMU_API=https://laniaamu.poggers.fi/api/v1 npm run dev
 */
function leaderboardBaseUrl(): string {
  const override = import.meta.env.VITE_LANIAAMU_API;
  if (typeof override === 'string' && override.length > 0) return override;
  return import.meta.env.DEV ? '' : LEADERBOARD.baseUrl;
}

/** How long the speedometer stays red after something took vauhti off you. */
const MOMENTUM_FLASH_SECONDS = 0.45;

/** How long a fetched leaderboard is considered current. */
const BOARD_STALE_SECONDS = 30;

/**
 * Where the cook is standing, or null on a level with no muurikka on it.
 *
 * Read from the level rather than typed out beside his line, so the trigger
 * follows the pan if the pan ever moves. See `KOKKI_LINE`.
 */
const KOKKI_AT = TEST_LEVEL.muurikka?.cook.stand ?? null;

/**
 * And where Mäysä has to be walked into before he says anything, or null on a
 * level with nobody in the porstua.
 *
 * A box rather than a radius, unlike the cook's. The porstua is 1.7 m of floor
 * with open yard on both sides of it, so a circle round the man also covers
 * the veräjä the runner arrives from and the paving outside his own east wall,
 * which is the way everybody goes to the pan - and being shouted at out there
 * is being told about a wall nobody has hit. The box is the doorway he fills,
 * and its near edge is the far side of the man asleep across the mouth of the
 * porch: clearing him is what trying this door consists of, and until it has
 * been done there is nothing to explain. See `MAYSA_LINE`.
 *
 * Read out of the level like `KOKKI_AT` is, so the trigger follows the porch
 * and the sleeper if either ever moves.
 */
function maysaPorch(): { minX: number; maxX: number; pastZ: number } | null {
  const maysa = TEST_LEVEL.aamupala?.maysa;
  if (!maysa) return null;
  const doorway = TEST_LEVEL.solids.find((item) => item.name === maysa.gate);
  const doorstep = TEST_LEVEL.sleepers.find((item) => item.name === maysa.doorstep);
  if (!doorway || !doorstep) return null;
  return {
    minX: doorway.box.minX,
    maxX: doorway.box.maxX,
    pastZ: sleeperBox(doorstep).minZ,
  };
}

const MAYSA_PORCH = maysaPorch();

/**
 * The id of the one door in the venue that does not open, or null on a level
 * with no sauna behind its end wall.
 *
 * Read out of the level like the two above rather than typed beside Kempf's
 * line, so the answer follows the leaf if it is ever slid along the wall or
 * given a different name. See `SaunaSpec` and src/world/Sauna.ts.
 */
const SAUNA_DOOR_ID = TEST_LEVEL.sauna?.id ?? null;

/**
 * How long the boxes stay up after the third makkara lands.
 *
 * Render-only, and short: it is there so the plate can be *seen* to fill
 * rather than to vanish on the frame it does. Long enough to read, over before
 * the runner is through the door.
 */
const AAMUPALA_HOLD_SECONDS = 1.1;

/**
 * Which menu page is open on top of whatever screen the run state is showing.
 *
 * Deliberately not a `RunState`. Reading the settings does not stop a run any
 * more than reading them changes one - the run is already stopped by the time
 * a menu can be clicked, because a menu needs the mouse and the game has it.
 */
type MenuPage = 'none' | 'settings' | 'help' | 'results' | 'splits';

interface DemoOptions {
  replay: Replay;
  onExit: () => void;
}

export class Game {
  private readonly objective = new RunObjective();
  readonly level: LevelDefinition;
  readonly seed: number;
  private readonly fingerprint: string;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly composer: EffectComposer;
  private readonly psychedelic = new PsychedelicPass();
  private readonly viewmodel: BeerViewmodel;
  /** The split log in Kimmo's other hand, between the kasa and the hatch. */
  private readonly klapi: KlapiViewmodel;
  private readonly plate: PlateViewmodel;
  private readonly tuoppi: TuoppiViewmodel;
  /** Thrown once a run at most, at the pirtti's window. */
  private readonly fist: FistViewmodel;
  /** The clip on the valkokangas. Null on a level with no projector in it. */
  private readonly projection: Projection | null;
  private readonly world: World;
  private readonly simulation: RunSimulation;
  private readonly cameraController: CameraController;
  private readonly runManager: RunManager;
  private readonly input: InputManager;
  /** Raw mouse angle. The simulation only ever sees a quantised snapshot. */
  private readonly look = new LookAccumulator();
  private readonly recorder = new ReplayRecorder();
  private readonly personalRuns = new PersonalRuns();
  private readonly resultsScreen = new ResultsScreen(
    () => this.closeMenu(), (id) => { void this.watchReplay(id); },
  );
  private readonly leaderboardSplitsScreen = new LeaderboardSplitsScreen(() => this.closeMenu());
  private replayRequest: AbortController | null = null;
  private demoGame: Game | null = null;
  private demoContainer: HTMLElement | null = null;
  private demoFrame = 0;
  private readonly demoStatus = el('div', 'demo-status');

  private readonly leaderboard: LeaderboardClient;
  private readonly submitter: RunSubmitter;
  private readonly settings = new SettingsStore();
  private readonly audio: AudioManager = new WebAudioManager();
  private readonly loop: GameLoop;

  private readonly hud = new HUD();
  /**
   * What the window costs, on the screen. Deliberately not part of
   * `refreshScreens()`: it answers to how long ago the glass went and to
   * nothing else, so there is no state for two places to disagree about.
   */
  private readonly blood = new BloodOverlay();
  private readonly wakeOverlay = new WakeUpOverlay();
  private wakeUp: WakeUpSequence | null = null;
  private wakeRecordingStarted = false;
  /**
   * The three boxes at the muurikka, the plate, and what happens if the wrong
   * one goes in. Outside `refreshScreens()` like the blood, and for the same
   * reason: it draws what the simulation says the pan is doing and decides
   * nothing of its own.
   */
  private readonly aamupala = new AamupalaOverlay();
  private readonly computer = new ComputerScreen(
    (key) => {
      if (this.runManager.state === RunState.Running && this.simulation.finishSeat.isSeated) {
        this.pendingPin.push(key);
      }
    },
  );
  private readonly pendingPin: number[] = [];
  /**
   * The menus. Every button on them ends up asking for the mouse back or giving
   * it away, which is why they are wired to `input` rather than to each other.
   */
  private readonly readyScreen = new ReadyScreen({
    start: () => this.input.requestPointerLock(),
    openResults: () => this.openMenu('results'),
    openSettings: () => this.openMenu('settings'),
    openHelp: () => this.openMenu('help'),
  });
  private readonly finishScreen = new FinishScreen({
    restart: () => this.restartAndPlay(),
    returnToMenu: () => {
      this.closeMenu();
      this.restart();
      this.input.exitPointerLock();
    },
  });
  private readonly pauseScreen = new PauseScreen({
    openResults: () => this.openMenu('results'),
    resume: () => {
      if (this.simulation.finishSeat.isSeated) this.runManager.resume();
      else this.input.requestPointerLock();
    },
    openSettings: () => this.openMenu('settings'),
    openHelp: () => this.openMenu('help'),
    restart: () => this.restartAndPlay(),
  });
  private readonly settingsScreen: ReturnType<typeof createSettingsScreen>;
  private readonly helpScreen = createHelpScreen(() => this.closeMenu());
  private readonly debugOverlay = new DebugOverlay();
  /** Who is talking and when. See `src/game/ShoutDirector.ts`. */
  private readonly shouts = new ShoutDirector(this.audio);

  /** Open menu page, if any. See `MenuPage`. */
  private menu: MenuPage = 'none';

  private readonly interpolated: Vec3 = vec3();
  private focused: Interactable | null = null;
  private renderTime = 0;
  private finishedAtRenderTime: number | null = null;
  /** Presentation timestamp; the simulation already reports the cable contact. */
  private powerCutAt: number | null = null;
  private pendingFlash: string | null = null;
  /** Render time the HUD stops showing the speedometer in "you lost it" red. */
  private momentumLostUntil = 0;
  /**
   * Render time the next fart is due, or null while nobody is snoring. Render
   * time rather than simulation time because the clock it belongs to is the
   * one that runs on the READY screen: the simulation is not stepping there,
   * so a fart scheduled against it would never come due.
   */
  private fartDueAt: number | null = null;
  /**
   * Simulation time the plate filled, or null. The boxes are held on screen for
   * `AAMUPALA_HOLD_SECONDS` past it - see `render()`.
   */
  private aamupalaDoneAt: number | null = null;
  /**
   * Simulation time Kimmo last ate a vegetable, or null. Drives the mess on the
   * screen exactly the way `Ikkuna.brokeAt` drives the blood.
   */
  private spewedAt: number | null = null;
  /** Index into the scripted stumble schedule, for the scuffing sound. */
  private nextStumble = 0;
  /** F was pressed since the last step. Consumed by exactly one step. */
  private pendingAction = false;
  /** And E, the same way. */
  private pendingSip = false;
  /** Which box was grabbed at since the last step, 0..2, or -1. Same contract. */
  private pendingPick = -1;
  /** The run id the current recording was begun under. '' when offline. */
  private recordingRunId = '';
  /** Which category the visible board is showing, so it is not refetched. */
  private boardCategory: string | null = null;
  /** When it was last fetched, so a LAN full of people still sees each other. */
  private boardFetchedAt = 0;
  /**
   * Mouse sensitivity as of the start of the attempt.
   *
   * Frozen for the duration of a run, and written into the replay header. The
   * submission analysis checks that every look delta is a whole number of mouse
   * pixels at this sensitivity; if the slider could move mid-run, half the run
   * would be on a different grid and an honest player would be flagged for it.
   * Changing the slider mid-attempt therefore takes effect on the next one.
   */
  private runMouseSensitivity = 1;

  /** The simulation owns these; the aliases keep the render code readable. */
  private get player() {
    return this.simulation.player;
  }

  private get hannu() {
    return this.simulation.hannu;
  }

  constructor(private readonly container: HTMLElement, seed: number = DEFAULT_SEED,
    private readonly demo?: DemoOptions) {
    this.seed = seed;
    this.level = createSeededLevel(this.seed);
    this.fingerprint = simulationFingerprint(this.level);
    this.settingsScreen = createSettingsScreen(this.settings, () => this.closeMenu(), {
      value: this.seed,
      change: (seed) => {
        const url = new URL(window.location.href);
        url.searchParams.set('seed', String(seed));
        window.location.assign(url.href);
      },
    });
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      this.settings.values.fieldOfView,
      container.clientWidth / Math.max(1, container.clientHeight),
      RENDER.near,
      RENDER.far,
    );
    this.cameraController = new CameraController(this.camera);

    const dose = this.doseFromSettings();

    this.runManager = new RunManager({
      seed: this.seed,
      fingerprint: this.fingerprint,
      checkpoints: this.level.checkpoints,
      repository: demo ? new NullScoreRepository() : new LocalStorageScoreRepository(),
      playerName: PLAYER_NAME,
      levelName: this.level.name,
      intoxication: dose,
      // Stamped onto the result so the finish screen knows whether this attempt
      // is even eligible for the board.
      runMetadata: () => ({
        runId: this.recordingRunId,
        replayFrames: this.recorder.frameCount,
      }),
    });

    // The simulation builds the collision world; World only draws it.
    this.simulation = new RunSimulation(this.level, this.runManager, demo?.replay.header.pinSeed ?? (demo ? 0 : this.newPinSeed()));
    // Capped rather than maxed: the yard is mostly seen at a glancing angle at
    // speed, which is exactly what anisotropy is for, but 16x on a laptop iGPU
    // buys nothing you can see at 30 km/h.
    this.world = new World(
      this.level,
      this.simulation.collision,
      Math.min(8, this.renderer.capabilities.getMaxAnisotropy()),
    );
    this.look.reset(this.level.spawn.yaw);

    // The bottle hangs off the camera, and three.js only draws the graph it
    // walks - so the camera itself has to be in the scene for its children to
    // exist as far as the renderer is concerned.
    this.viewmodel = new BeerViewmodel(this.camera, this.renderer.capabilities.getMaxAnisotropy());
    this.klapi = new KlapiViewmodel(this.camera);
    this.plate = new PlateViewmodel(this.camera);
    this.tuoppi = new TuoppiViewmodel(this.camera);
    this.fist = new FistViewmodel(this.camera);
    this.world.scene.add(this.camera);

    // The projector, which is scenery with a video decoder attached: it starts
    // when the page does, it is not part of the world's geometry, and the one
    // thing it hands back is "the clip just passed its cue" - see render().
    this.projection = this.level.projection
      ? new Projection(this.level.projection, PROJECTOR.cueAt)
      : null;
    if (this.projection) this.world.scene.add(this.projection.mesh);

    // A backend is optional. Without one the game is identical apart from the
    // shared board - which is the right trade for a LAN with flaky internet.
    this.leaderboard = leaderboardBaseUrl()
      ? new HttpLeaderboardClient(leaderboardBaseUrl())
      : new OfflineLeaderboardClient();
    this.submitter = new RunSubmitter(this.leaderboard);
    this.submitter.subscribe((state) => this.onSubmissionState(state));

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.world.scene, this.camera));
    this.composer.addPass(this.psychedelic);

    this.input = new InputManager(container);

    this.hud.mount(container);
    this.blood.mount(container);
    this.wakeOverlay.mount(container);
    this.aamupala.mount(container);
    this.computer.mount(container);
    this.readyScreen.mount(container);
    this.finishScreen.mount(this.computer.browserContent);
    this.pauseScreen.mount(container);
    this.settingsScreen.mount(container);
    this.helpScreen.mount(container);
    this.resultsScreen.mount(container);
    this.leaderboardSplitsScreen.mount(container);
    for (const board of [this.readyScreen.board, this.finishScreen.board]) {
      board.onSelect = (entry) => {
        this.leaderboardSplitsScreen.show(entry);
        this.openMenu('splits');
      };
    }
    if (demo) {
      this.demoStatus.append(el('span', 'demo-status__text', 'DEMO · ESC palaa tuloksiin'),
        button('TAKAISIN', demo.onExit, 'btn', 'ESC'));
      container.append(this.demoStatus);
      this.computer.root.inert = true;
    }
    this.debugOverlay.mount(container);

    this.loop = new GameLoop({
      preUpdate: (frameSeconds) => this.preUpdate(frameSeconds),
      step: () => this.step(),
      render: (alpha, frameSeconds) => this.render(alpha, frameSeconds),
    });

    this.applySettings();
    this.settings.subscribe(() => this.applySettings());
  }

  async start(): Promise<void> {
    if (this.demo) window.addEventListener('keydown', this.onDemoKey, true);
    else this.input.attach();
    this.input.setPointerLockListener((locked) => this.onPointerLock(locked));
    window.addEventListener('resize', this.onResize);
    this.onResize();
    await this.runManager.init();
    if (this.demo) {
      this.runManager.start();
      this.audio.startLoop('ambient-morning');
      this.audio.startLoop('karaoke');
      this.audio.startLoop('teltta-dj');
      this.audio.startLoop('muurikka');
      this.audio.startLoop('running');
    }
    this.loop.start();
    // Fetch the run ticket and the board while the player reads the title, so
    // neither the start of a run nor the finish line ever waits on the network.
    if (!this.demo) void this.prepareForRun();
  }

  dispose(): void {
    this.replayRequest?.abort();
    this.demoGame?.dispose();
    this.demoContainer?.remove();
    window.removeEventListener('keydown', this.onDemoKey, true);
    this.loop.stop();
    this.input.detach();
    this.audio.dispose();
    this.viewmodel.dispose();
    this.klapi.dispose();
    this.plate.dispose();
    this.tuoppi.dispose();
    this.fist.dispose();
    this.projection?.dispose();
    this.world.chargeSparks?.dispose();
    window.removeEventListener('resize', this.onResize);
    this.composer.dispose();
    this.psychedelic.dispose();
    // A watched demo owns a complete scene; release its GPU resources on exit.
    const textures = new Set<THREE.Texture>();
    this.world.scene.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) {
        for (const value of Object.values(material)) {
          if (value instanceof THREE.Texture) textures.add(value);
        }
        material.dispose();
      }
    });
    textures.forEach((texture) => texture.dispose());
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }

  /**
   * Development handle. Exposed on `window.laniaamu` by main.ts in dev builds so
   * gameplay values can be poked at from the console while tuning:
   *
   *   laniaamu.debug.player.state.position.x = 120
   *   laniaamu.debug.settings.update({ cameraSwayIntensity: 0 })
   *   laniaamu.debug.step(120); laniaamu.debug.draw()   // frame-step by hand
   */
  get debug() {
    return {
      world: this.world,
      player: this.player,
      runManager: this.runManager,
      settings: this.settings,
      loop: this.loop,
      camera: this.camera,
      renderer: this.renderer,
      hannu: this.hannu,
      audio: this.audio,
      simulation: this.simulation,
      submitter: this.submitter,
      /** The recording of the attempt currently in progress. */
      replay: () => this.recorder.snapshot(),
      /**
       * Re-simulate the current recording right here, the same way the server
       * would. Handy when a run gets rejected and you want to know why.
       */
      verifyReplay: () => {
        const replay = this.recorder.snapshot();
        return replay ? verifyReplay(replay) : null;
      },
      restart: () => this.restart(),
      /** Advance the simulation by N fixed steps without waiting for frames. */
      step: (count = 1) => {
        for (let i = 0; i < count; i++) this.step();
      },
      /**
       * Draw one frame on demand (useful when rAF is throttled).
       *
       * `frameSeconds` is what render-rate animation is advanced by - the
       * bottle, the fades - so hand-stepping the viewmodel means passing a
       * plausible frame time rather than leaving it at zero:
       *
       *   for (let i = 0; i < 60; i++) { laniaamu.debug.step(2); laniaamu.debug.draw(0, 1/60); }
       */
      draw: (alpha = 0, frameSeconds = 0) => this.render(alpha, frameSeconds),
      /** Process queued input/actions without waiting for a frame. */
      pumpInput: () => this.preUpdate(0),
    };
  }

  /* ---------------------------------------------------------------- menu -- */

  private openMenu(page: MenuPage): void {
    this.menu = page;
    if (page === 'results') {
      this.resultsScreen.setStatus('');
      this.refreshPersonalRuns();
    }
    // A menu is a mouse UI. Nothing can click it while the game owns the
    // cursor, so taking the cursor back is part of opening one - even though
    // in practice the mouse is already free by the time a button was clicked.
    this.input.exitPointerLock();
  }

  private closeMenu(): void {
    this.replayRequest?.abort();
    this.replayRequest = null;
    this.menu = 'none';
  }

  /**
   * Which menu the arrow keys and Enter belong to right now, if any.
   *
   * Every screen with buttons on it is one a player can reach without a mouse
   * in hand, so all of them answer the arrows - but exactly one at a time. The
   * keys are queued regardless (see `InputManager`, where the arrows are also
   * Kimmo's legs) and the one this returns is the only one that gets them.
   *
   * The order is the order the screens stack in: behind pointer lock there is
   * no menu at all and the arrows are legs again, a settings or help card is on
   * top of whatever opened it, and otherwise the run's own state says which
   * screen is up. `refreshScreens()` decides what is *visible* off the same two
   * facts, and these two must not be allowed to disagree.
   */
  private get keyboardMenu(): MenuButtons | null {
    if (this.input.isPointerLocked) return null;
    if (this.menu === 'settings') return this.settingsScreen.buttons;
    if (this.menu === 'help') return this.helpScreen.buttons;
    if (this.menu === 'results') return this.resultsScreen.buttons;
    if (this.menu === 'splits') return this.leaderboardSplitsScreen.buttons;
    switch (this.runManager.state) {
      case RunState.Ready:
        return this.readyScreen.buttons;
      case RunState.Paused:
        return this.pauseScreen.buttons;
      case RunState.Finished:
        return this.computer.resultsReady ? this.finishScreen.buttons : null;
      default:
        return null;
    }
  }

  /**
   * Esc means "back", not "settings".
   *
   * The old binding opened the settings from anywhere, which is the one thing
   * Esc does not mean anywhere else: it closes what is open. So it closes the
   * menu page if there is one, and otherwise stops the run and hands the mouse
   * back - which lands you on the screen the buttons are on.
   *
   * The browser has usually released pointer lock by itself before this runs
   * (that is what its own Esc does), and `onPointerLock` would then pause a
   * running attempt anyway. Pausing here as well is not redundant: the order of
   * the keydown and the pointerlockchange event is not ours to rely on.
   */
  private onEscape(): void {
    if (this.menu !== 'none') {
      this.closeMenu();
      return;
    }
    if (this.runManager.state === RunState.Running) this.runManager.pause();
    this.input.exitPointerLock();
  }

  private refreshPersonalRuns(): void {
    this.resultsScreen.show(
      this.personalRuns.top(this.runManager.category, this.runManager.personalBest),
      `${describeIntoxication(this.runManager.intoxicationLevel)} · Seed ${this.seed}`,
    );
  }

  private async watchReplay(id: string): Promise<void> {
    this.replayRequest?.abort();
    const request = new AbortController();
    this.replayRequest = request;
    this.resultsScreen.setStatus('Ladataan ja tarkistetaan replayta…', true);
    try {
      if (!id) throw new Error('Syötä replay-id.');
      let replay = this.personalRuns.replay(id);
      if (!replay) {
        const url = this.leaderboard.replayUrl(id);
        if (!url) throw new Error('Tunniste on virheellinen tai replay ei ole saatavilla tässä selaimessa.');
        replay = await loadReplay(url, request.signal);
      }
      if (request.signal.aborted || this.menu !== 'results') return;
      const verdict = verifyReplay(replay);
      if (!verdict.ok) throw new Error(verdict.message);
      this.resultsScreen.setStatus('');
      this.startDemo(replay);
    } catch (error) {
      if (!request.signal.aborted) this.resultsScreen.setStatus(
        error instanceof Error ? error.message : 'Replayn lataus epäonnistui.',
      );
    } finally {
      if (this.replayRequest === request) this.replayRequest = null;
    }
  }

  private startDemo(replay: Replay): void {
    this.input.releaseAll();
    this.input.consumeActions();
    this.input.detach();
    this.loop.stop();
    this.audio.setSuspended(true);
    this.projection?.setSuspended(true);
    this.container.style.visibility = 'hidden';
    const container = el('div', 'demo-container pointer-free');
    this.container.parentElement!.append(container);
    this.demoContainer = container;
    this.refreshScreens();
    try {
      this.demoGame = new Game(container, replay.header.seed, { replay, onExit: () => this.closeDemo() });
      void this.demoGame.start().catch(() => {
        this.closeDemo();
        this.resultsScreen.setStatus('Demon käynnistys epäonnistui.');
      });
    } catch (error) {
      this.closeDemo();
      throw error;
    }
  }

  private closeDemo(): void {
    this.demoGame?.dispose();
    this.demoGame = null;
    this.demoContainer?.remove();
    this.demoContainer = null;
    this.container.style.visibility = '';
    this.input.releaseAll();
    this.input.consumeActions();
    this.input.attach();
    this.audio.setSuspended(false);
    this.projection?.setSuspended(false);
    this.loop.start();
  }

  private readonly onDemoKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !event.repeat) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.demo?.onExit();
    }
  };

  /* ----------------------------------------------------------- lifecycle -- */

  private newPinSeed(): number {
    return crypto.getRandomValues(new Uint32Array(1))[0];
  }

  /** R: full state reset without touching the scene graph or reloading. */
  restart(): void {
    if (this.demo) return;
    // Read before the reset, because the reset is what makes it READY.
    const wasReady = this.runManager.state === RunState.Ready;
    // Staying paused matters when this restart came from a dose change made in
    // the pause menu - see RunManager.reset().
    this.runManager.reset(true);
    this.simulation.reset(this.newPinSeed());
    this.pendingPin.length = 0;
    this.look.reset(this.level.spawn.yaw);
    this.hud.clearFlash();
    this.objective.reset();
    this.finishedAtRenderTime = null;
    this.powerCutAt = null;
    this.world.chargeSparks?.render(null);
    this.pendingFlash = null;
    this.momentumLostUntil = 0;
    this.pendingAction = false;
    this.pendingSip = false;
    this.pendingPick = -1;
    this.focused = null;
    this.shouts.reset();
    this.recordingRunId = '';
    this.viewmodel.reset();
    this.klapi.reset();
    this.plate.reset();
    this.tuoppi.reset();
    this.fist.reset();
    // The cable is back in its socket, so the lights are back on. Mirrors
    // `RunSimulation.reset()`, which is what actually cleared the latch.
    this.world.setPowerOn(true);
    this.projection?.setPowerOn(true);
    // A restart from the READY screen is not silencing a run: nothing is
    // playing but that screen's own snoring, and `refreshScreens` asks for it
    // straight back on the next frame. Stopping it would start a second copy
    // over the one still fading out - harmless once, but R held down restarts
    // on every repeat, and the copies stack into a crackle.
    if (wasReady) this.audio.stopSpeech();
    else this.audio.stopAll();
    void this.prepareForRun();
  }

  /**
   * Restart from a menu button rather than from the R key.
   *
   * The difference is the mouse: R is pressed by someone whose hands are
   * already in the game, while a button is clicked by someone whose cursor is
   * out of it. Asking for pointer lock straight back means the new attempt is
   * one key press away instead of one click and one key press.
   */
  private restartAndPlay(): void {
    this.closeMenu();
    this.restart();
    this.input.requestPointerLock();
  }

  /**
   * Everything the *next* attempt needs from the network, done up front:
   * a single-use run ticket, and the board for the dose currently set.
   */
  private async prepareForRun(): Promise<void> {
    const category = this.runManager.category;
    await this.submitter.prepare(category);
    await this.refreshBoard(category);
  }

  /**
   * Opens a recording for the attempt that is about to start.
   *
   * The header is fixed at this moment on purpose: the dose, the physics
   * fingerprint and the mouse sensitivity all describe *this* run, and none of
   * them may change under it. (The dose cannot - changing it restarts.)
   */
  private beginRecording(): void {
    this.recordingRunId = this.submitter.runId;
    this.runMouseSensitivity = this.settings.values.mouseSensitivity;
    const dose = this.runManager.intoxicationLevel;
    this.recorder.begin({
      pinSeed: this.simulation.pin.seed,
      seed: this.seed,
      runId: this.recordingRunId,
      fingerprint: this.fingerprint,
      levelName: this.level.name,
      alcoholPromille: dose.alcoholPromille,
      drugsPercent: dose.drugsPercent,
      simulationHz: SIMULATION_HZ,
      mouseSensitivity: this.runMouseSensitivity,
      startedAt: Date.now(),
    });
  }

  private onFinished(finished: FinishedRun): void {
    this.finishScreen.show(finished, this.runManager.personalBest);
    if (this.demo) {
      this.demoStatus.querySelector('span')!.textContent = 'DEMO PÄÄTTYI · ESC palaa tuloksiin';
      this.audio.stopAll();
      return;
    }
    const localId = this.personalRuns.save(finished.result, this.recorder.snapshot());
    // The run is over, so the mouse goes back to the player: the finish screen
    // has buttons on it, and the most common reason a run is unranked is a
    // missing name the player is now being told about.
    this.input.exitPointerLock();
    this.audio.play('finish');
    this.finishScreen.showSubmission(
      this.submitter.state,
      hasChosenName(this.settings.values.playerName),
    );
    void this.sendRun(finished, localId);
  }

  /**
   * Sends the attempt up, then refreshes the board so the player can see where
   * they landed. Never throws: a failed submission is a line of text, not a
   * broken finish screen.
   */
  private async sendRun(finished: FinishedRun, localId: string): Promise<void> {
    const name = this.settings.values.playerName;
    if (!hasChosenName(name)) return;

    const replay = this.recorder.snapshot();
    const state = await this.submitter.submit(finished.result, replay, name);
    if (state.status === 'accepted') {
      if (state.replayId) this.personalRuns.share(localId, state.replayId);
      if (this.menu === 'results' && !this.demoGame) this.refreshPersonalRuns();
      this.finishScreen.board.highlight(state.entry.runId);
      await this.refreshBoard(this.runManager.category, true);
    }
  }

  private onSubmissionState(state: SubmissionState): void {
    this.finishScreen.showSubmission(state, hasChosenName(this.settings.values.playerName));
  }

  /**
   * Refetches the board when the dose changes, when this player has just
   * submitted, or when what is on screen has gone stale.
   *
   * The staleness window exists because `R` is pressed constantly - one request
   * per restart would be absurd - but at a LAN somebody else finishing a run is
   * exactly what you want to see when you come back to the READY screen.
   */
  private async refreshBoard(category: string, force = false): Promise<void> {
    const stale = this.renderTime - this.boardFetchedAt > BOARD_STALE_SECONDS;
    if (!force && !stale && this.boardCategory === category) return;
    this.boardCategory = category;
    this.boardFetchedAt = this.renderTime;

    for (const board of [this.readyScreen.board, this.finishScreen.board]) {
      board.setCategoryLabel(`${describeIntoxication(this.runManager.intoxicationLevel)} · Seed ${this.seed}`);
      board.setStatus(this.leaderboard.isAvailable ? 'loading' : 'offline');
    }
    if (!this.leaderboard.isAvailable) return;

    const page = await this.leaderboard.top(category, LEADERBOARD.topCount);
    // The dose may have changed while the request was in flight.
    if (this.boardCategory !== category) return;
    this.readyScreen.board.render(page.entries, String(this.seed));
    this.finishScreen.board.render(page.entries, String(this.seed));
  }

  private onPointerLock(locked: boolean): void {
    if (locked) {
      this.runManager.resume();
      if (this.simulation.finishSeat.isSeated) this.input.exitPointerLock();
    } else if (this.runManager.state === RunState.Running && !this.simulation.finishSeat.isSeated) {
      this.runManager.pause();
    }
    this.container.classList.toggle('pointer-free', !locked);
  }

  private doseFromSettings(): IntoxicationLevel {
    if (this.demo) return sanitiseLevel(this.demo.replay.header);
    return sanitiseLevel({
      alcoholPromille: this.settings.values.alcoholPromille,
      drugsPercent: this.settings.values.drugsPercent,
    });
  }

  private applySettings(): void {
    const values = this.settings.values;
    this.audio.setEnabled(values.audioEnabled);
    this.debugOverlay.setVisible(values.showDebugOverlay);
    this.world.setCollidersVisible(values.showColliders);

    // The dose is part of the run's identity. Changing it mid-run would break
    // determinism, so it takes effect by restarting - and it moves you onto a
    // different leaderboard.
    const dose = this.doseFromSettings();
    const current = this.runManager.intoxicationLevel;
    if (
      dose.alcoholPromille !== current.alcoholPromille ||
      dose.drugsPercent !== current.drugsPercent
    ) {
      this.simulation.setIntoxication(dose);
      void this.runManager.setIntoxication(dose);
      // Changing the dose changes the category, so the board and the ticket
      // both have to follow. restart() re-requests both.
      this.restart();
    }
  }

  /** Cheap enough to run every frame; see the call site in render(). */
  private refreshScreens(): void {
    const state = this.runManager.state;
    // A menu page replaces the screen it was opened from rather than stacking
    // on it: two dimmed overlays deep, the game underneath is gone anyway.
    const bare = this.menu === 'none' && !this.demo;
    this.readyScreen.setVisible(state === RunState.Ready && bare);
    if (state === RunState.Ready) {
      this.readyScreen.update(
        this.runManager.personalBest,
        this.input.isPointerLocked,
        this.runManager.intoxicationLevel,
        this.settings.values.playerName,
        this.seed,
      );
      // The main screen has one sound in it, and it is Kimmo: he is still
      // asleep, which is the whole reason the CTA says *herättääksesi* rather
      // than aloittaa. So it plays under exactly that CTA and not a moment
      // earlier - pointer lock is what ALOITA buys, and until then this is a
      // front page somebody is reading, not a room somebody is asleep in. The
      // first noise the game makes should be one the player asked for. ESC
      // hands the mouse back to the menu and takes the snoring with it, the
      // same way it takes the CTA. Asked for every frame like the overlays
      // above, for the same reason - the mixer ignores a loop it is already
      // playing, and ignores a stop for one it is not.
      if (this.input.isPointerLocked) {
        this.audio.startLoop('ambient-snoring');
        // And every ten seconds of it, the other noise he makes. Scheduled off
        // the last one rather than off a wall clock so that ESC and back does
        // not walk into one immediately: the countdown starts when the snoring
        // does, which means the first thing the screen says is always the
        // snore. See `SNORE.fartInterval` for why ten and not four.
        if (this.fartDueAt === null) this.fartDueAt = this.renderTime + SNORE.fartInterval;
        else if (this.renderTime >= this.fartDueAt) {
          this.audio.play('fart');
          this.fartDueAt = this.renderTime + SNORE.fartInterval;
        }
      } else {
        this.audio.stopLoop('ambient-snoring');
        this.fartDueAt = null;
      }
    }
    // Kimmo is in his bed for exactly as long as the READY screen is up. Driven
    // off the state rather than off the transition, like the overlays: the one
    // thing that must never happen is a run with a body left lying in the bed.
    this.world.setSleepingPlayerVisible(state === RunState.Ready);
    this.computer.setVisible(this.simulation.finishSeat.isSeated && (bare || !!this.demo) &&
      (state === RunState.Running || state === RunState.Finished));
    this.finishScreen.setVisible(state === RunState.Finished && this.computer.resultsReady && (bare || !!this.demo));
    this.pauseScreen.setVisible(state === RunState.Paused && bare);
    this.settingsScreen.setVisible(this.menu === 'settings');
    this.helpScreen.setVisible(this.menu === 'help');
    this.resultsScreen.setVisible(this.menu === 'results' && !this.demoContainer);
    this.leaderboardSplitsScreen.setVisible(this.menu === 'splits');
    // Driven from the state every frame for the same reason the overlays are:
    // a transition that is missed cannot leave the mixer running under a menu.
    this.audio.setSuspended(state === RunState.Paused || !!this.demoContainer);
    // Including the picture: a video that keeps playing behind the pause menu
    // is a video that fires its cue into a mixer that is not listening.
    this.projection?.setSuspended(state === RunState.Paused || !!this.demoContainer);
  }

  /* -------------------------------------------------------------- frame --- */

  private preUpdate(_frameSeconds: number): void {
    if (this.demo) return;
    const state = this.runManager.state;

    // Mouse look accumulates at frame rate into a raw float angle. The
    // simulation reads a quantised snapshot of it once per fixed step, which is
    // what makes the look track recordable; the camera keeps using the raw
    // value, so nothing about the feel changes.
    const delta = this.input.consumeMouseDelta();
    if ((state === RunState.Running || state === RunState.Ready) &&
      (!this.wakeUp || this.wakeUp.done) && this.input.isPointerLocked &&
      !this.simulation.finishSeat.isSeated) {
      if (delta.dx !== 0 || delta.dy !== 0) {
        const sensitivity =
          state === RunState.Running
            ? this.runMouseSensitivity
            : this.settings.values.mouseSensitivity;
        const dy = this.settings.values.invertMouse ? -delta.dy : delta.dy;
        this.look.apply(delta.dx, dy, sensitivity);
      }
    }

    // Outside a running run nothing steps, so the player state would never pick
    // the look up - and then the interaction prompt would not follow the mouse
    // on the READY screen. Mirror it by hand.
    if (state !== RunState.Running) {
      this.player.state.yaw = this.look.yaw;
      this.player.state.pitch = this.look.pitch;
    }

    for (const action of this.input.consumeActions()) {
      switch (action) {
        case 'restart':
          if (!(this.runManager.state === RunState.Ready && this.input.isPointerLocked)) {
            this.restart();
          }
          break;
        case 'pause':
          this.onEscape();
          break;
        case 'toggleDebug':
          this.settings.update({ showDebugOverlay: !this.settings.values.showDebugOverlay });
          break;
        case 'toggleColliders':
          this.settings.update({ showColliders: !this.settings.values.showColliders });
          break;
        case 'action':
          // Queued rather than applied: interaction is now a bit on the next
          // simulation step, so it lands on an exact step and is recorded like
          // every other input.
          this.pendingAction = true;
          break;
        case 'sip':
          this.pendingSip = true;
          break;
        // Queued the same way F and E are, and for the same reason: which box
        // was grabbed at is a bit on a numbered step, not something that
        // happens at frame rate. The last of three pressed inside one step
        // wins, which is the only thing it can mean - a hand cannot press two
        // boxes in a 120th of a second and mean both.
        case 'pick1':
          this.pendingPick = 0;
          break;
        case 'pick2':
          this.pendingPick = 1;
          break;
        case 'pick3':
          this.pendingPick = 2;
          break;
        case 'menuUp':
          this.keyboardMenu?.move(-1);
          break;
        case 'menuDown':
          this.keyboardMenu?.move(1);
          break;
        case 'menuSelect':
          this.keyboardMenu?.activate();
          break;
      }
    }

    // READY -> RUNNING on the first key press, except Escape.
    if (
      this.runManager.state === RunState.Ready &&
      this.input.isPointerLocked &&
      this.input.hasStartInput()
    ) {
      if (this.runManager.start()) this.wakeUp = new WakeUpSequence();
      this.audio.startLoop('ambient-morning');
      // And the snoring stops, because it was Kimmo's: a key woke him, so the
      // sound he was making is over. It is the main screen's own loop and it
      // is started nowhere else - a run is not a room anybody is asleep in.
      this.audio.stopLoop('ambient-snoring');
      // Including the fart that was already scheduled. Cleared rather than left
      // to expire: a run is forty seconds long, and a countdown still holding a
      // render time from before it would fire on the first frame of the next
      // READY screen instead of ten seconds into it.
      this.fartDueAt = null;

      // Somebody has had the mic in the juhlasali all night and has no
      // intention of giving it up. Started here rather than at a checkpoint on
      // purpose: it is 130 m away and effectively silent, and the whole point
      // is that it grows on its own as the run gets closer. See config/audio.
      this.audio.startLoop('karaoke');
      // And the decks in the XLMOTO tent, which are the same idea aimed the
      // other way: that one is inaudible until the last thirty metres, this
      // one is a thump under the birds from the first frame of the run and 97
      // metres out. Both are one loop and a distance, and neither is a cue.
      this.audio.startLoop('teltta-dj');
      // And the ruokatiimi's pan on the kivetys, which is the same idea a third
      // time and at a twentieth of the range: this one is inaudible until the
      // veräjä and full only where the game makes you stand still for it. It is
      // started here with the other two rather than at the checkpoint before it
      // for the same reason they are - it is one loop and a distance, and a
      // sizzle that switches on has been switched on. See MUURIKKA_SIHINA.
      this.audio.startLoop('muurikka');
      // The feet. A loop rather than a cue per step, resampled to whatever the
      // vauhti meter is doing - see RUN_LOOP in config/audio.
      this.audio.startLoop('running');
    }

    // F, E and the number row pressed outside a run do nothing, and must not
    // be saved up.
    if (this.runManager.state !== RunState.Running || !this.wakeUp?.done) {
      this.pendingAction = false;
      this.pendingSip = false;
      this.pendingPick = -1;
    }

    if (this.runManager.state === RunState.Running && this.wakeUp?.done && !this.wakeRecordingStarted) {
      this.beginRecording();
      this.wakeRecordingStarted = true;
    }
    this.focused = this.simulation.focusedInteractable();
  }

  /**
   * What the player is doing, right now, as one recordable record.
   *
   * This is the only place the browser's input state is turned into gameplay
   * input, and the value it returns is *both* what the simulation is stepped
   * with and what goes into the replay. They cannot drift apart because there
   * is only one of them.
   */
  private buildStepInput(): StepInput {
    const action = this.pendingAction;
    const sip = this.pendingSip;
    const pick = this.pendingPick;
    this.pendingAction = false;
    this.pendingSip = false;
    this.pendingPick = -1;
    return {
      buttons: this.simulation.finishSeat.isSeated
        ? buttonForPin(this.pendingPin.shift() ?? -1)
        : buttonsFromMovement(this.input.movement, action, sip, pick),
      yawQ: this.look.yawUnits,
      pitchQ: this.look.pitchUnits,
    };
  }

  private step(): void {
    if (this.runManager.state !== RunState.Running) return;
    if (!this.demo && (!this.wakeUp?.done || !this.wakeRecordingStarted)) return;

    const simulationTime = this.runManager.simulationTime;

    const input = this.demo ? this.demo.replay.frames[this.demoFrame++] : this.buildStepInput();
    if (!input) return;
    if (!this.demo) this.recorder.record(input);
    const events = this.simulation.step(input);
    if (this.demo) this.look.reset(this.player.state.yaw, this.player.state.pitch);
    if (events.interacted === 'oma-paikka') {
      this.finishedAtRenderTime = this.renderTime;
      this.input.releaseAll();
      this.input.exitPointerLock();
    }

    if (events.klapi) {
      // The klapi has its own two sounds, so it must not also play a door.
      this.audio.play(events.klapi === 'taken' ? 'klapi-nosto' : 'klapi-pesaan');
    } else if (events.tuoppi) {
      // And so does the tuoppi, for the same reason and in the same shape: two
      // presses of F, and neither of them is a door.
      //
      // `hana-auki` goes with the *press* rather than with the full glass,
      // because it is the one cue in the game that is a process: it runs the
      // length of `SIMA.pourDuration` and its last sound is the handle coming
      // back up. Played on `'poured'` it would start where it should finish.
      if (events.tuoppi === 'taken') {
        this.audio.play('tuoppi-poydalta');
        // The juhlasali floor is at world y = 0, also when picked up mid-jump.
        this.viewmodel.drop(this.world.scene);
      }
      else if (events.tuoppi === 'pouring') this.audio.play('hana-auki');
      // The corridor opening is worth the same noise a checkpoint is - the
      // same treatment the third makkara gets, and for the same reason: it is
      // the moment the last room stops being a problem. It lands on the step
      // the glass comes up full, which is what the player has been watching
      // climb the glass in the corner of the screen.
      else this.audio.play('checkpoint');
    } else if (events.interacted) {
      if (events.interacted === SAUNA_DOOR_ID) {
        // The one press in the game that changes nothing. It gets a noise of
        // its own anyway - a latch going nowhere - because the alternative is
        // a key that did something and a game that said so with silence; and
        // then the man beside it says why, which is the actual answer. See
        // src/world/Sauna.ts and `KEMPF_LINE`.
        this.audio.play('ovi-lukossa');
        this.shouts.saunaTried(simulationTime);
      } else if (events.door) {
        // A door has two sounds now, because it has two halves: F reverses the
        // leaf wherever it is, so the press that shuts one has to sound like a
        // door shutting rather than like the same door opening a second time.
        // See src/world/Door.ts.
        this.audio.play(events.door === 'opened' ? 'door-open' : 'door-close');
      } else if (events.interacted !== 'oma-paikka') {
        this.audio.play('door-open');
      }
    }
    // Louder for a bigger pull, so the ear gets the same warning the hands are
    // about to get. See src/config/beer.ts.
    if (events.sipped) {
      this.audio.play('beer-sip', clamp(0.55 + events.sipped.strength * 0.45, 0, 1));
      if (this.simulation.sima?.holdingTuoppi) this.tuoppi.sip();
      else this.viewmodel.sip();
    }
    if (events.finished) {
      this.onFinished(events.finished);
      return;
    }

    if (events.hannuEmerged) this.audio.play('hannu-emerge');
    if (events.hannuHit) this.audio.play('hannu-swipe');

    // The bemari's charging cable, out of its socket and taking the venue's
    // power with it. The car sparks at the same instant as the audible cut.
    if (events.powerCut) {
      this.powerCutAt = simulationTime;
      this.audio.play('power-off');
      this.world.setPowerOn(false);
      // The projector is on the same mains as everything else in the hall.
      this.projection?.setPowerOn(false);
      // And so is the karaoke rig: the PA at the hall door dies with the
      // socket, mid-word. Nothing restarts it mid-run - the cut is a latch
      // until reset(), and the next run's first input is what starts the
      // loop again.
      this.audio.stopLoop('karaoke');
      // And so does the tent, which is fed off the same tolppa the cable came
      // out of - it is a pop-up on somebody's lawn, not a stage with its own
      // supply. The set stops mid-bar behind you at the exact moment the yard
      // goes dark, which is the whole of what the blackout is: everything
      // somebody plugged in last night, at once. The light show goes with it -
      // see `World.setPowerOn`.
      this.audio.stopLoop('teltta-dj');
      // `muurikka` is deliberately not in this list. Everything else somebody
      // plugged in last night goes at once, and the pan does not: it is a gas
      // ring with a bottle under it, so the last stretch of a run that pulled
      // the cable is a dark yard with birds in it and one thing still cooking.
      // That is worth a line here rather than a silence, because the obvious
      // reading of the two calls above is that they are the list of everything
      // in the yard that makes a noise.
      // Let the audible power-off sound finish before Kimmo reacts and the tent replies.
      this.shouts.powerCut(simulationTime);
    }


    this.shouts.step(simulationTime, events.hannuShouted);

    // The pirtti's window, on the one step it stops being one - which is now
    // the step F went into it rather than the step a body crossed it. The
    // shards are read back out of the simulation in render() the way the fire
    // is; this is the noise and the hand that made it, and both fire on the
    // step rather than on the frame so that two machines playing one replay
    // hear it in the same place.
    if (events.glassBroke) {
      this.audio.play('glass-break');
      this.fist.punch();
      this.shouts.glassBroke(simulationTime);
    }

    // The muurikka's three answers: a makkara on the plate, a mouthful of
    // parsakaalia, and the third one landing. All three are played off the
    // step rather than off the frame, so two machines playing one replay hear
    // them in the same place - the same treatment the glass gets.
    const breakfast = events.aamupala;
    if (breakfast) {
      if (breakfast.served) this.audio.play('makkara-lautaselle');
      if (breakfast.spewed) {
        this.audio.play('oksennus');
        this.spewedAt = simulationTime;
      }
      if (breakfast.done) {
        this.aamupalaDoneAt = simulationTime;
        this.shouts.breakfastDone(simulationTime);
        // The door being clear is worth the same noise a checkpoint is: it is
        // the moment the yard stops being a problem.
        this.audio.play('checkpoint');
      }
    }

    // And Mäysä, the moment Kimmo lands in the porch at a door that is not
    // going to open. Off the player's position like the cook's line below, and
    // only while there is still something in the way - once he has stepped
    // aside there is nothing left for him to explain.
    //
    // Not on the way up the porch, though: he waits until the hop over the man
    // in the mouth has been made and the runner is between the porch's walls,
    // which is the first moment the door has actually been tried. See
    // `MAYSA_PORCH`.
    if (MAYSA_PORCH && this.simulation.aamupala?.isDone === false) {
      const { x, z } = this.player.state.position;
      if (z <= MAYSA_PORCH.pastZ && x >= MAYSA_PORCH.minX && x <= MAYSA_PORCH.maxX) {
        this.shouts.seeMaysa(simulationTime);
      }
    }

    // And the cook at the muurikka, as Kimmo passes to the right of the porch.
    // Off the player's position rather than off the clock, and stepped rather
    // than rendered, so two machines playing one replay hear it on the same
    // step - the same treatment the yell above gets, and for the same reason.
    // Nothing here touches the simulation: see `KOKKI_LINE`.
    // The radius also covers Mäysä's doorway: wait on the cook's side of the
    // porch so a direct approach to the door only triggers Mäysä's reminder.
    if (KOKKI_AT && (!MAYSA_PORCH || this.player.state.position.x > MAYSA_PORCH.maxX)) {
      const dx = this.player.state.position.x - KOKKI_AT[0];
      const dz = this.player.state.position.z - KOKKI_AT[1];
      if (dx * dx + dz * dz <= KOKKI_LINE.radius * KOKKI_LINE.radius) {
        this.shouts.seeKokki(simulationTime);
      }
    }

    for (const split of events.splits) {
      if (split.id === 'FINISH') continue;
      this.pendingFlash = split.label;
      this.audio.play('checkpoint');

      // And the porukka out on the hall's floor, the moment its doorway is
      // behind him. Off the split rather than off a circle round them: what
      // the line asks for is a glass and a hana, and both of those are passed
      // on the way to the people asking, so it has to land at the door rather
      // than at their shoulders. See `PORUKKA_LINE`.
      if (split.id === 'LAN_ROOM_ENTRY' && this.simulation.sima) {
        this.shouts.enterHall(simulationTime);
      }
    }

    // Scripted stumbles are known in advance, so the scuff is scheduled off the
    // same list the movement uses rather than sniffed out of the velocity.
    const stumbles = this.player.intoxication.stumbles;
    const drunk = this.player.intoxication.alcoholStrength;
    while (this.nextStumble < stumbles.length && stumbles[this.nextStumble].startTime <= simulationTime) {
      const stumble = stumbles[this.nextStumble];
      this.nextStumble++;
      if (drunk > 0.05) this.audio.play('stumble', clamp(stumble.strength * drunk, 0.2, 1));
    }

    if (events.jumped) this.audio.play('jump');
    if (events.doubleJumped) this.audio.play('double-jump');
    if (events.landed) this.audio.play('land');
    if (events.impactSpeed > 1.5) this.audio.play('bump', clamp(events.impactSpeed / 6, 0, 1));

    // Flash the speedometer for whatever just cost vauhti. Driven off the same
    // events the simulation charged for rather than off watching the number
    // fall, so idling to a stop does not read as a penalty.
    if (
      events.jumped ||
      events.doubleJumped ||
      events.hannuHit ||
      events.impactSpeed >= MOMENTUM.hitImpactSpeed
    ) {
      this.momentumLostUntil = this.renderTime + MOMENTUM_FLASH_SECONDS;
    }

  }

  private render(alpha: number, frameSeconds: number): void {
    this.renderTime += frameSeconds;

    const state = this.runManager.state;
    const settings = this.settings.values;
    const simulationTime = this.runManager.simulationTime;

    // Sampling the intoxication at the interpolated render time keeps the camera
    // smooth on high-refresh displays without letting rendering touch gameplay.
    const renderSimTime = Math.max(0, simulationTime - (1 - alpha) * FIXED_TIMESTEP);
    const intox = this.player.intoxication.sample(renderSimTime);

    this.player.interpolatedPosition(alpha, this.interpolated);
    // Where the ears are. The karaoke gets louder because this moved, not
    // because anything told it to - rendering reads the position, the mixer
    // decides what it sounds like, and neither touches the simulation.
    this.audio.setListener(this.interpolated.x, this.interpolated.z);
    // And how fast they are moving, which is what the run loop is resampled to.
    // Ground speed only: falling is not running, whatever the speedometer says.
    const velocity = this.player.state.velocity;
    this.audio.setPace(
      state === RunState.Running ? Math.hypot(velocity.x, velocity.z) : 0,
    );

    // Advance presentation only. The next frame may start recording after
    // this frame has actually shown the fully opened view.
    if (this.wakeUp && state === RunState.Running) {
      const cue = this.wakeUp.advance(frameSeconds, this.audio.isSpeaking(RUN_START_LINES[0].id));
      if (cue === 'shout') {
        const line = RUN_START_LINES[0];
        this.audio.speak({ id: line.id, text: line.text, cue: line.cue, ...line.voice });
      } else if (cue === 'rise') this.audio.play('kimmo-wake');
    }
    const wakeUpProgress = this.demo ? 1 : this.wakeUp?.progress ?? 0;
    this.wakeOverlay.render(this.wakeUp && !this.wakeUp.done ? this.wakeUp.visual : null);
    this.renderer.domElement.style.filter = this.wakeUp && !this.wakeUp.done
      ? `blur(${this.wakeUp.visual.blur}px)` : '';
    const sit =
      this.finishedAtRenderTime === null
        ? 0
        : sitProgress(this.renderTime - this.finishedAtRenderTime);

    this.cameraController.update({
      position: this.interpolated,
      // The camera follows the raw mouse angle rather than the quantised one
      // the simulation steps by. Same value to five decimal places, but it
      // updates on frames that took no simulation step at all - which is what
      // keeps a 240 Hz display smooth over a 120 Hz simulation.
      yaw: this.look.yaw,
      pitch: this.look.pitch,
      speed: this.player.speed,
      intox,
      swayIntensity: settings.cameraSwayIntensity * (1 - sit),
      wakeUpProgress,
      sitProgress: sit,
      seat: {
        eye: this.level.finish.seatEye,
        yaw: this.level.finish.seatYaw,
        pitch: this.level.finish.seatPitch,
      },
      fov: settings.fieldOfView,
    });

    // Where the eye is decides how much of the blackout is showing, so this
    // rides with the rest of the interpolated camera work rather than with the
    // step that cut the power.
    this.world.renderPower(this.interpolated);
    this.world.renderDoors(this.simulation.doors);
    // And the one that does not open: the man beside it puts a hand up at
    // whoever tried the handle, and takes it down again. Off simulation time
    // like the blood and the shards, so a pause holds the hand where it is.
    this.world.renderGreeting(this.shouts.greetingAge(simulationTime));
    this.world.renderHannu(this.hannu.state, this.renderTime, this.camera.position);
    this.world.chargeSparks?.render(
      this.powerCutAt === null ? null : simulationTime - this.powerCutAt,
    );
    // The fire, and the veräjä a klapi in it opens. Both read simulation state
    // back out and neither writes to it - the same split the doors use.
    //
    // The fire is on `lastKlapiAt` and the gate is on `fedAt`, and those are
    // two different moments now: the first log lights the pesä and moves
    // nothing else. That gap is on purpose - it is the only thing in the yard
    // that says "that one counted, and it was not enough" without spending a
    // line of UI on it.
    this.world.renderFire(
      this.renderTime,
      this.simulation.kamiina.lastKlapiAt === null
        ? null
        : simulationTime - this.simulation.kamiina.lastKlapiAt,
    );
    this.world.renderVeraja(this.simulation.kamiina.gateOpenProgress);
    // The window: still glass, or however long ago it stopped being glass.
    const brokeAt = this.simulation.ikkuna.brokenAt;
    this.world.renderIkkuna(
      brokeAt === null ? null : simulationTime - brokeAt,
      this.simulation.ikkuna.impact,
    );
    // And the same break from the other side of the glass. Off the same age as
    // the shards, so the blood is up for exactly the three seconds `GLASS_PAIN`
    // is charging for - and off simulation time rather than render time, so a
    // pause holds it where it is instead of running it out behind a menu.
    this.blood.render(brokeAt === null ? null : simulationTime - brokeAt);

    // And the muurikka. The boxes are up whenever the pan is working and for a
    // beat after the plate fills; the mess is up for exactly as long as the
    // pause it is reporting, which is what `AamupalaOverlay` is handed rather
    // than told.
    const pan = this.simulation.aamupala?.view() ?? null;
    const showPan =
      pan !== null &&
      pan.phase !== 'idle' &&
      (this.aamupalaDoneAt === null ||
        simulationTime - this.aamupalaDoneAt < AAMUPALA_HOLD_SECONDS);
    this.aamupala.render(
      showPan ? pan : null,
      this.spewedAt === null ? null : simulationTime - this.spewedAt,
      simulationTime,
      this.runManager.intoxicationLevel,
    );
    // Which is also what the pan itself is doing, out in the world: the lasta
    // sweeps while the boxes are mixing, and Mäysä is where the plate says he
    // is. Read-only, like everything else in render().
    this.world.setAamupala(pan);
    // And the same for the drink: whether the glass is still on the stack, and
    // whether the corner at the end of the hall is still full of people.
    const sima = this.simulation.sima;
    this.world.setSima(
      sima ? { taken: sima.holdingTuoppi || sima.isPoured, poured: sima.isPoured } : null,
    );
    this.world.renderSmoke(this.renderTime);
    // And the rig in the XLMOTO tent, off the same clock and for the same
    // reason: the party has been going since before the page loaded and does
    // not rewind because somebody pressed R.
    this.world.renderDisco(this.renderTime);
    // And the four in front of it, on the record's own beat rather than on the
    // rig's - the lamps and the people are two things happening to the same
    // track. See `src/render/Dance.ts`.
    this.world.renderCrowd(this.renderTime, this.interpolated);
    this.world.renderStanders(this.camera.position);

    // The clip on the valkokangas reaching the moment somebody in the hall
    // reacts to. Heard only from inside the navetta: it is a man shouting at a
    // screen in a room, not a line aimed at the player, and out in the yard
    // you are simply not in the room it happens in.
    if (this.projection?.update() && insideVenue(this.interpolated.x, this.interpolated.z)) {
      this.audio.play('ajotus');
    }

    // And the clip's own soundtrack under it, at whatever the room leaves of
    // it. `MIX.master` by hand because this one sound does not pass through the
    // mixer's master gain - see `Projection.setVolume` for why it stays in the
    // video element - and zero when the game is muted, which is the other thing
    // that gain node would otherwise have done for us.
    this.projection?.setVolume(
      this.settings.values.audioEnabled
        ? MIX.master *
            PROJECTOR.gain *
            placedMix(this.interpolated.x, this.interpolated.z, PROJECTOR.source).gain
        : 0,
    );

    // The bottle answers the lurch the simulation is already applying, rather
    // than a copy of its envelope - so the arm swings on the exact frames the
    // legs do. Read-only, like everything else in render().
    const lurch = sampleBeer(this.player.state.beer);
    const bottleHitFloor = this.viewmodel.update({
      dt: frameSeconds,
      // Only mid-run: every other state has a panel over the screen, and a
      // bottle behind a leaderboard is just something in the way.
      visible: state === RunState.Running,
      yaw: this.look.yaw,
      pitch: this.look.pitch,
      speed: this.player.speed,
      bobLateral: intox.bobLateral,
      bobVertical: intox.bobVertical,
      swayIntensity: settings.cameraSwayIntensity,
      lurchX: lurch.wish.x,
      lurchY: lurch.wish.y,
    });
    if (bottleHitFloor) this.audio.play('bottle-break');

    this.klapi.update({
      dt: frameSeconds,
      holding: state === RunState.Running && this.simulation.kamiina.holdingKlapi,
      speed: this.player.speed,
      bobLateral: intox.bobLateral,
      bobVertical: intox.bobVertical,
      swayIntensity: settings.cameraSwayIntensity,
    });

    // Keep the breakfast in the left hand all the way to Kimmo's seat.
    this.plate.update({
      dt: frameSeconds,
      visible: state === RunState.Running,
      breakfast: pan,
      speed: this.player.speed,
      bobLateral: intox.bobLateral,
      bobVertical: intox.bobVertical,
      swayIntensity: settings.cameraSwayIntensity,
    });

    // The mug replaces the released bottle in the right hand.
    this.tuoppi.update({
      dt: frameSeconds,
      holding: state === RunState.Running && sima !== null && sima.holdingTuoppi,
      fill: sima?.fill ?? 0,
      speed: this.player.speed,
      bobLateral: intox.bobLateral,
      bobVertical: intox.bobVertical,
      swayIntensity: settings.cameraSwayIntensity,
    });

    // The punch runs on render time rather than on simulation time, like every
    // other thing in a hand: it is 0.4 s of animation started by one step, and
    // a run stepped at half speed in the debug console should still see a fist
    // thrown at the speed a fist is thrown.
    this.fist.update({ dt: frameSeconds });

    // The acid pass costs a full-screen draw, so at 0 % we skip it entirely and
    // render straight to the canvas - a sober run is pixel-for-pixel plain.
    if (intox.psychedelic.intensity > 0) {
      this.psychedelic.apply(
        intox.psychedelic,
        renderSimTime,
        PSYCHEDELIC.warpFrequency,
        PSYCHEDELIC.warpSpeed,
        PSYCHEDELIC.rippleFrequency,
      );
      this.composer.render();
    } else {
      this.renderer.render(this.world.scene, this.camera);
    }

    if (this.pendingFlash) {
      this.hud.flashCheckpoint(this.pendingFlash, this.renderTime);
      this.pendingFlash = null;
    }

    this.hud.update({
      state,
      pinComplete: this.simulation.pin.complete,
      objective: this.objective.update({
        now: simulationTime,
        atPalju: this.hannu.state.emergedAt !== null || this.simulation.kamiina.burnedKlapit > 0,
        logs: this.simulation.kamiina.burnedKlapit,
        atBreakfast: pan !== null && pan.phase !== 'idle',
        servings: pan?.plate ?? 0,
        inLanRoom: this.shouts.sawPorukka,
        poured: sima?.isPoured ?? false,
      }),
      elapsedMs: this.wakeUp && !this.wakeUp.done
        ? this.wakeUp.countdownMs : this.runManager.elapsedMs,
      prompt: this.focused?.prompt ?? null,
      splits: this.runManager.checkpoints.reached,
      personalBestSplits: this.runManager.personalBest?.splits ?? [],
      nowSeconds: this.renderTime,
      shout: this.shouts.current(
        simulationTime,
        state === RunState.Running,
        this.wakeUp?.shouting === true,
        this.simulation.aamupala?.isDone === true,
      ),
      speed: this.player.speed,
      momentum: this.player.momentum01,
      momentumLost: this.renderTime < this.momentumLostUntil,
      beer: this.player.beerBoost01,
      alcoholPromille: this.player.intoxication.currentLevel.alcoholPromille,
    });

    // Driven every frame rather than on transitions: overlay visibility is then
    // impossible to get out of sync with the state machine.
    this.computer.update(this.simulation.pin, simulationTime,
      this.runManager.intoxicationLevel, this.runManager.elapsedMs, this.renderTime);
    this.refreshScreens();

    this.debugOverlay.update({
      fps: this.loop.currentFps,
      frameSeconds: this.loop.lastFrameSeconds,
      stepsLastFrame: this.loop.lastStepCount,
      accumulator: this.loop.accumulatorSeconds,
      simulationTime,
      elapsedMs: this.runManager.elapsedMs,
      state,
      player: this.player.state,
      speed: this.player.speed,
      intox,
      nextCheckpoint: this.runManager.checkpoints.nextCheckpoint,
      timeToNextStumble: this.player.intoxication.timeToNextStumble(simulationTime),
      collidersVisible: this.world.collidersVisible,
      colliderCount: this.world.collision.all.length,
      samples: this.audio.sampleStatus(),
      intoxication: this.player.intoxication.currentLevel,
      alcoholStrength: this.player.intoxication.alcoholStrength,
      drugStrength: this.player.intoxication.drugStrength,
      hannu: {
        phase: this.hannu.state.phase,
        distance: this.hannu.distanceTo(this.player.state.position),
        swipes: this.hannu.state.swipes,
      },
    });
  }

  /**
   * What is being yelled right now. Kimmo's brief reaction stays readable;
   * otherwise Hannu wins because he has a hand out. Everybody wins over the intro,
   * because that one is shouted from another building.
   */

  private readonly onResize = (): void => {
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    // updateStyle must stay on: the CSS is injected asynchronously in dev, so
    // the first measurement can be stale and the inline size would stick.
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);
    // The blood is painted in CSS pixels on a canvas of its own, so it needs
    // the new size in the same breath - the marks are placed in fractions of
    // the screen, and a stale one would put them somewhere else.
    this.blood.setSize(width, height);
    this.aamupala.setSize(width, height);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  };
}
